// apps/admin/src/features/billing/IssueCreditMemoModal.tsx
//
// Phase 8.2 Wave 3 Task 18 — Extracted Issue Credit Memo modal sibling.
//
// vg-binding: CONTEXT:decisions=P8.D-485,P8.D-490,P8.D-496
//
//  (A5 Credit Memos surface) +
//      (perm split — `credit_memo:issue:up_to_500` kế toán default
//               `credit_memo:issue:unlimited` admin) +
//      (banner: kế toán > $500 → PENDING_APPROVAL queue).
// Covers goals.
//
// Endpoint consumed:
//   POST /api/v1/admin/credit-memos (Idempotency-Key required)
//
// Permission gate (FE hint — BE is authoritative):
//   - Caller passes `userPermissions` + `userRoles[]`
//   - hasAmountGate('credit_memo:issue:up_to_500', amount) gates kế toán
//   - hasPermission(user, perms, 'credit_memo:issue:unlimited') unlocks admin
//   - Above $500 with kế toán → form still submits but warns the user that
//     the BE will route to PENDING_APPROVAL (banner copy).
//
// Test IDs (per task-18 spec):
//   issue-credit-memo-modal, issue-credit-memo-amount-input,
//   credit-memo-issue-submit-btn, credit-memo-issue-threshold-warning,
//   credit-memo-issue-notes-input, im-merchant

import { useMemo, useState } from 'react';
import type { JSX } from 'react';
import { Link } from 'react-router-dom';

import { apiClient } from '@printway/shared/api';
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  MerchantPicker,
  useToast,
} from '@printway/ui';
import { useTranslation } from '../../i18n/useTranslation.js';

import { PERMISSIONS, hasAmountGate } from '../../lib/permissions.js';

// ---------------------------------------------------------------------------
// Constants / helpers
// ---------------------------------------------------------------------------

const ACCOUNTANT_ISSUE_THRESHOLD_CENTS = 50_000; // $500.00

interface ApiErrorEnvelope {
  ok?: false;
  success?: false;
  error?: {
    code?: string;
    message?: string;
    user_message?: string;
    field_errors?: Record<string, string[] | string>;
  };
}

type LooseClient = {
  POST: (
    path: string,
    init: { body?: unknown; headers?: Record<string, string> },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
};

function extractApiMessage(payload: unknown, fallback: string): string {
  if (payload && typeof payload === 'object') {
    const env = payload as ApiErrorEnvelope;
    if (env.error?.user_message) return env.error.user_message;
    if (env.error?.message) return env.error.message;
  }
  return fallback;
}

function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `idemp-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface IssueCreditMemoModalProps {
  open: boolean;
  onClose: () => void;
  /** Pre-selected merchant when opened from a merchant-scoped context. */
  defaultMerchantId?: string;
  /** Caller refreshes the parent list after a successful submit. */
  onIssued?: (payload: { requiresApproval: boolean }) => void;
  /** Permission keys the user holds — used for FE-side amount-gate hints. */
  userPermissions?: ReadonlyArray<string>;
  /**
   * True when current user is in the kế toán role. When true, amounts
   * above $500 trigger the PENDING_APPROVAL banner.
   */
  isAccountant?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function IssueCreditMemoModal({
  open,
  onClose,
  defaultMerchantId = '',
  onIssued,
  userPermissions = [],
  isAccountant = false,
}: IssueCreditMemoModalProps): JSX.Element | null {
  const { t } = useTranslation('finance');
  const { toast } = useToast();

  const [merchantId, setMerchantId] = useState<string>(defaultMerchantId);
  const [merchantLabel, setMerchantLabel] = useState<string | null>(null);
  const [amountStr, setAmountStr] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Reset form when modal opens.
  useMemoOnOpen(open, () => {
    setMerchantId(defaultMerchantId);
    setAmountStr('');
    setNotes('');
    setFieldErrors({});
  });

  const amountCents = useMemo(() => {
    const parsed = parseFloat(amountStr);
    if (!Number.isFinite(parsed) || parsed <= 0) return 0;
    return Math.round(parsed * 100);
  }, [amountStr]);

  // Permission-aware threshold check.
  // - Admin with `credit_memo:issue:unlimited` → no cap.
  // - Kế toán with `credit_memo:issue:up_to_500` → cap at $500; above warns
  //   but still submits (BE routes to PENDING_APPROVAL).
  const hasUnlimitedGrant = userPermissions.includes(
    PERMISSIONS.CREDIT_MEMO_ISSUE_UNLIMITED,
  );

  // For accountant role: FE knows amount > $500 will route to PENDING_APPROVAL.
  // For unlimited-grant users (admin): no threshold warning.
  const overAccountantThreshold =
    isAccountant &&
    !hasUnlimitedGrant &&
    amountCents > ACCOUNTANT_ISSUE_THRESHOLD_CENTS;

  // Defensive — if user has neither grant, FE blocks submit and tells them.
  const lacksAnyIssueGrant =
    !hasUnlimitedGrant &&
    !hasAmountGate(PERMISSIONS.CREDIT_MEMO_ISSUE_UP_TO_500, amountCents);

  // over-threshold accountant routes to PENDING_APPROVAL queue
  // so the BE accepts the submit even though the accountant's own grant
  // (`credit_memo:issue:up_to_500`) does not cover the amount. The
  // threshold warning above signals the routing change to the user; here
  // we exempt the over-threshold path from the lacks-grant block.
  const submitDisabled =
    submitting ||
    amountCents <= 0 ||
    notes.trim().length < 3 ||
    !merchantId ||
    (isAccountant && lacksAnyIssueGrant && !overAccountantThreshold);

  async function submit(): Promise<void> {
    setFieldErrors({});
    if (!merchantId) {
      setFieldErrors({ merchant_id: t('credit_memo.issue.errMerchantRequired') });
      return;
    }
    if (amountCents <= 0) {
      setFieldErrors({ amount_usd_cents: t('credit_memo.issue.errAmountPositive') });
      return;
    }
    if (notes.trim().length < 3) {
      setFieldErrors({ notes: t('credit_memo.issue.errNotesMin') });
      return;
    }

    setSubmitting(true);
    try {
      const client = apiClient() as unknown as LooseClient;
      const { data, error, response } = await client.POST(
        '/api/v1/admin/credit-memos',
        {
          body: {
            merchant_id: merchantId,
            amount_usd_cents: amountCents,
            notes: notes.trim(),
            issue_type: 'MANUAL_GOODWILL' as const,
            original_order_id: null,
            refund_tx_group_id: null,
          },
          headers: { 'Idempotency-Key': newIdempotencyKey() },
        },
      );

      if (error || !response.ok) {
        const env = (error ?? {}) as ApiErrorEnvelope;
        if (env.error?.field_errors) {
          const flat: Record<string, string> = {};
          for (const [k, v] of Object.entries(env.error.field_errors)) {
            flat[k] = Array.isArray(v) ? (v[0] ?? '') : String(v);
          }
          setFieldErrors(flat);
        }
        toast({
          title: t('credit_memo.issue.toastFailed'),
          description: extractApiMessage(error, t('credit_memo.toast.genericError')),
          variant: 'error',
        });
        return;
      }

      // Read `requires_admin_approval` so the success toast matches the BE
      // routing decision (banner copy).
      const payload = data as
        | { ok?: true; data?: { requires_admin_approval?: boolean } }
        | { requires_admin_approval?: boolean }
        | undefined;
      const inner =
        payload && typeof payload === 'object' && 'data' in payload && payload.data
          ? payload.data
          : (payload as { requires_admin_approval?: boolean } | undefined);
      const requiresApproval = Boolean(inner?.requires_admin_approval);

      toast({
        title: requiresApproval
          ? t('credit_memo.issue.toastPendingTitle')
          : t('credit_memo.issue.toastIssuedTitle'),
        description: requiresApproval
          ? t('credit_memo.issue.toastPendingDesc')
          : t('credit_memo.issue.toastIssuedDesc'),
        variant: 'success',
      });
      onIssued?.({ requiresApproval });
      onClose();
    } catch (err) {
      toast({
        title: t('credit_memo.toast.networkError'),
        description:
          err instanceof Error && err.message
            ? err.message
            : t('credit_memo.toast.networkErrorDesc'),
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      className="max-w-lg"
      data-testid="issue-credit-memo-modal"
    >
      <DialogHeader>
        <DialogTitle>{t('credit_memo.actions.issue')}</DialogTitle>
      </DialogHeader>
      <form
        data-testid="credit-memo-issue-form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="mt-4 flex flex-col gap-4 px-6 pb-4"
      >
        {/* Merchant */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="im-merchant" className="text-sm font-medium text-pw-text">
            {t('credit_memo.columns.merchant')} <span className="text-red-600 dark:text-red-300">*</span>
          </label>
          <MerchantPicker
            value={merchantId || null}
            valueLabel={merchantLabel}
            onChange={(id, label) => {
              setMerchantId(id ?? '');
              setMerchantLabel(label);
            }}
            billingMode="INVOICE"
            placeholder={t('credit_memo.issue.merchantPlaceholder')}
            testId="im-merchant"
          />
          {fieldErrors.merchant_id ? (
            <p className="text-xs text-red-600 dark:text-red-300">{fieldErrors.merchant_id}</p>
          ) : null}
          <p className="text-xs text-pw-text-muted">
            {t('credit_memo.issue.merchantHint')}
          </p>
        </div>

        {/* Amount */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="im-amount" className="text-sm font-medium text-pw-text">
            {t('credit_memo.apply.amountLabel')} <span className="text-red-600 dark:text-red-300">*</span>
          </label>
          <Input
            id="im-amount"
            data-testid="issue-credit-memo-amount-input"
            type="number"
            step="0.01"
            min="0.01"
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value)}
            aria-invalid={Boolean(fieldErrors.amount_usd_cents)}
          />
          {fieldErrors.amount_usd_cents ? (
            <p className="text-xs text-red-600 dark:text-red-300">{fieldErrors.amount_usd_cents}</p>
          ) : null}
          {overAccountantThreshold ? (
            <div
              className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-300"
              data-testid="credit-memo-issue-threshold-warning"
              role="alert"
            >
              <strong>{t('credit_memo.issue.thresholdTitle')}</strong>{' '}
              {t('credit_memo.issue.thresholdBefore')}{' '}
              {/* Route lives under /admin (App.tsx registers
                  /admin/billing/credit-memo-approvals); the old plain <a>
                  pointed at /billing/... → 404 + full app reload
                  (wallet-handover mục 14.2). */}
              <Link
                to="/admin/billing/credit-memo-approvals"
                className="underline hover:text-amber-900"
              >
                {t('credit_memo.issue.thresholdQueueLink')}
              </Link>
              {t('credit_memo.issue.thresholdAfter')}
            </div>
          ) : null}
        </div>

        {/* Notes */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="im-notes" className="text-sm font-medium text-pw-text">
            {t('credit_memo.columns.notes')} <span className="text-red-600 dark:text-red-300">*</span>
          </label>
          <textarea
            id="im-notes"
            data-testid="credit-memo-issue-notes-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            minLength={3}
            maxLength={1000}
            aria-invalid={Boolean(fieldErrors.notes)}
            className="w-full rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-sm text-pw-text placeholder:text-pw-text-muted focus:border-pw-primary focus:outline-none focus:ring-1 focus:ring-pw-primary"
            placeholder={t('credit_memo.issue.notesPlaceholder')}
          />
          {fieldErrors.notes ? (
            <p className="text-xs text-red-600 dark:text-red-300">{fieldErrors.notes}</p>
          ) : null}
        </div>

        {/* Issue type — locked to MANUAL_GOODWILL per task spec. */}
        <div className="rounded-md border border-pw-border bg-pw-surface px-3 py-2 text-xs text-pw-text-muted">
          <strong className="text-pw-text">{t('credit_memo.issue.issueTypeLabel')}</strong>{' '}
          MANUAL_GOODWILL {t('credit_memo.issue.issueTypeHint')}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            type="button"
            onClick={onClose}
            disabled={submitting}
          >
            {t('credit_memo.actions.cancel')}
          </Button>
          <Button
            variant="default"
            type="submit"
            data-testid="credit-memo-issue-submit-btn"
            disabled={submitDisabled}
            title={
              overAccountantThreshold
                ? t('credit_memo.issue.submitTitleThreshold')
                : undefined
            }
          >
            {submitting
              ? t('credit_memo.issue.submitting')
              : overAccountantThreshold
                ? t('credit_memo.issue.submitForApproval')
                : t('credit_memo.issue.submitDefault')}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

export default IssueCreditMemoModal;

// ---------------------------------------------------------------------------
// Tiny helpers
// ---------------------------------------------------------------------------

/**
 * Run `effect` once whenever `open` transitions from false → true. Avoids
 * pulling in useEffect-with-deps boilerplate at every call site.
 */
function useMemoOnOpen(open: boolean, effect: () => void): void {
  // useMemo runs synchronously during render — fine for resetting local
  // state when the modal opens (no DOM side-effect required).
  useMemo(() => {
    if (open) effect();
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}
